import React, { useState } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { SUBSCRIPTION_PLANS } from '../../services/payment';
import { UserTier } from '../../types';

// Define local subscription plans for UI display
const UI_SUBSCRIPTION_PLANS: Record<string, {
  name: string;
  price: number;
  features: string[];
}> = {
  beta: {
    name: 'Beta',
    price: 10,
    features: [
      '3 DAY FREE TRIAL',
      '30 messages per hour',
      'More thorough responses'
    ]
  },
  alpha: {
    name: 'Alpha',
    price: 25,
    features: [
      '3 DAY FREE TRIAL',
      '100 messages per hour',
      'The most thorough responses',
      'VIP support'
    ]
  }
};

interface SubscriptionManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({
  isOpen,
  onClose,
}) => {
  const { tier } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Early return if not a paid tier
  if (!isOpen || tier === 'free') return null;

  const handleManageSubscription = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sessionToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Portal session error:', error);
      setError(error instanceof Error ? error.message : 'Failed to open customer portal');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/80" 
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-slate-700">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-100">
              Manage Your {UI_SUBSCRIPTION_PLANS[tier as 'beta' | 'alpha'].name} Subscription
            </h2>
            <p className="text-slate-300 mt-2">
              Current plan: ${UI_SUBSCRIPTION_PLANS[tier as 'beta' | 'alpha'].price}/month
            </p>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <div className="space-y-2">
              <h3 className="font-medium text-slate-100">Your Plan Features:</h3>
              <ul className="list-disc pl-5 text-slate-300 space-y-1">
                {UI_SUBSCRIPTION_PLANS[tier as 'beta' | 'alpha'].features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>

            {error && (
              <div className="text-red-400 text-sm bg-red-950/50 p-3 rounded-md">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md bg-slate-700 text-slate-100 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500"
            >
              Close
            </button>
            <button
              onClick={handleManageSubscription}
              className="px-4 py-2 rounded-md bg-emerald-500 text-slate-900 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Manage Subscription"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
